import React from 'react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CardVariant = 'default' | 'elevated' | 'bordered' | 'flat'
export type CardPadding = 'none' | 'sm' | 'md' | 'lg'

export interface CardProps {
  variant?: CardVariant
  padding?: CardPadding
  as?: React.ElementType
  className?: string
  children: React.ReactNode
}

export interface CardHeaderProps {
  title?: string
  subtitle?: string
  action?: React.ReactNode
  children?: React.ReactNode
}

export interface CardBodyProps {
  children: React.ReactNode
  noPadding?: boolean
}

export interface CardFooterProps {
  children: React.ReactNode
  divider?: boolean
}

// ---------------------------------------------------------------------------
// Internal context — shares padding down to sub-components
// ---------------------------------------------------------------------------

interface CardContextValue {
  padding: CardPadding
}

const CardContext = React.createContext<CardContextValue>({ padding: 'md' })

// ---------------------------------------------------------------------------
// Token maps
// ---------------------------------------------------------------------------

const variantClasses: Record<CardVariant, string> = {
  default: 'bg-neutral-0 border border-neutral-200 rounded-xl shadow-xs',
  elevated: 'bg-neutral-0 rounded-xl shadow-md',
  bordered: 'bg-neutral-0 border-2 border-neutral-300 rounded-xl',
  flat: 'bg-neutral-50 rounded-xl',
}

const paddingClasses: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
}

// ---------------------------------------------------------------------------
// CardHeader
// ---------------------------------------------------------------------------

function CardHeader({
  title,
  subtitle,
  action,
  children,
}: CardHeaderProps): React.ReactElement {
  const { padding } = React.useContext(CardContext)

  return (
    <div
      className={[
        'flex items-start justify-between',
        paddingClasses[padding],
      ].join(' ')}
    >
      {children ?? (
        <div className="flex-1 min-w-0">
          {title !== undefined && (
            <h3 className="text-lg font-semibold font-serif text-neutral-900">{title}</h3>
          )}
          {subtitle !== undefined && (
            <p className="text-sm text-neutral-600 mt-1">{subtitle}</p>
          )}
        </div>
      )}
      {action !== undefined && (
        <div className="ms-3 shrink-0">{action}</div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// CardBody
// ---------------------------------------------------------------------------

function CardBody({ children, noPadding = false }: CardBodyProps): React.ReactElement {
  const { padding } = React.useContext(CardContext)

  return (
    <div
      className={[
        'border-t border-neutral-100',
        noPadding ? 'p-0' : paddingClasses[padding],
      ].join(' ')}
    >
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// CardFooter
// ---------------------------------------------------------------------------

function CardFooter({ children, divider = false }: CardFooterProps): React.ReactElement {
  const { padding } = React.useContext(CardContext)

  return (
    <div
      className={[
        'flex items-center justify-end gap-3',
        paddingClasses[padding],
        divider ? 'border-t border-neutral-200' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Card (root) — compound component
// ---------------------------------------------------------------------------

type CardComponent = React.FC<CardProps> & {
  Header: React.FC<CardHeaderProps>
  Body: React.FC<CardBodyProps>
  Footer: React.FC<CardFooterProps>
}

export const Card: CardComponent = function Card({
  variant = 'default',
  padding = 'md',
  as: Tag = 'div',
  className,
  children,
}: CardProps): React.ReactElement {
  return (
    <CardContext.Provider value={{ padding }}>
      <Tag
        className={[variantClasses[variant], className].filter(Boolean).join(' ')}
      >
        {children}
      </Tag>
    </CardContext.Provider>
  )
}

Card.Header = CardHeader
Card.Body = CardBody
Card.Footer = CardFooter
